import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../../../styles/SolarStation.css";
import useLoader from "../../../../hooks/useLoader";
import { deleteData, fetchData, getCurrentData } from "../../../../utils/utils";
import useModal from "../../../../hooks/useModal";
import ViewModal from "../../../../components/commonComponent/ViewModal";
import Search from "../../../../components/commonComponent/Search";
import StatusToggleButton from "../../../../components/commonComponent/StatusToggleButton";
import Table from "../../../../components/commonComponent/Table";
import DeleteConfirmationModal from "../../../../components/commonComponent/DeleteConfirmationModal";
import Pagination from "../../../../components/commonComponent/Pagination";

const API_URL = process.env.REACT_APP_API_URL;

const ITEMS_PER_PAGE = 10;

const ProductCategory = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const { loading, showLoader, hideLoader } = useLoader();

  const {
    showDeleteModal,
    showViewModal,
    selectedItem,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleOpenViewModal,
    handleCloseViewModal,
  } = useModal();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchTerm.length >= 2 || searchTerm === "") {
      fetchData(
        `${API_URL}/productcategory/getAllproductcategories`,
        setData,
        setError,
        setFilteredData,
        currentPage,
        searchTerm,
        showLoader,
        hideLoader
      );
    }
  }, [currentPage, searchTerm]);

  const handleSearch = (searchValue) => {
    if (searchValue.length >= 2) {
      setSearchTerm(searchValue);
      setCurrentPage(1);
    } else if (searchValue.length === 0) {
      setSearchTerm("");
      setCurrentPage(1);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      await fetch(`${API_URL}/productcategory/updateproductcategory/${itemId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isActive: newStatus }),
      });
      setData((prevData) =>
        prevData.map((item) =>
          item._id === itemId ? { ...item, isActive: newStatus } : item
        )
      );
    } catch (error) {
      toast.error("Error updating status", { autoClose: 1000 });
    }
  };

  const handleDeleteConfirm = () => {
    deleteData(
      `${API_URL}/productcategory/deleteproductcategory`,
      selectedItem._id,
      setError
    )
      .then(() => {
        setData((prevData) =>
          prevData.filter((item) => item._id !== selectedItem._id)
        );
        fetchData(
          `${API_URL}/productcategory/getAllproductcategories`,
          setData,
          setError,
          setFilteredData,
          currentPage,
          searchTerm,
          showLoader,
          hideLoader
        );
        toast.success("Deleted successfully", { autoClose: 1000 });
      })
      .catch((error) => {
        setError(error);
        toast.error("Error deleting data", { autoClose: 1000 });
      });
    handleCloseDeleteModal();
  };

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const currentData = getCurrentData(filteredData, currentPage, ITEMS_PER_PAGE);

  const fieldsToShow = {
    Name: "name",
    Slug: "slug",
  };

  const columns = [
    { header: "Name", accessor: "name" },
    { header: "Slug", accessor: "slug" },
    {
      header: "Status",
      accessor: "isActive",
      render: (item) => (
        <StatusToggleButton
          isActive={item.isActive}
          itemId={item._id}
          onStatusChange={handleStatusChange}
        />
      ),
    },
  ];

  return (
    <div>
      <h2 className="capitalize font-bold text-[24px] mb-4">Products Category List</h2>

      <div className="flex justify-between items-center shadow-md px-8 py-[10px] rounded-md bg-[#fff] text-base mb-4">
        <div className="flex items-center">
          <div className="search-filter-container">
            <Search
              searchFields={["First Name", "Last Name", "Email"]}
              onSearch={handleSearch}
            />
          </div>
        </div>

        <Link
          to="/product-category/add"
          className="inline-flex items-center justify-center rounded-md border border-white px-4 py-1.5 text-center font-medium text-white bg-[#1C2434] hover:bg-opacity-90"
        >
          Add Product Category
        </Link>
      </div>

      <div className="table-container">
        <Table
          columns={columns}
          data={currentData}
          showIndex={true}
          currentPage={currentPage}
          itemsPerPage={ITEMS_PER_PAGE}
          onDelete={handleOpenDeleteModal}
          onUpdate={(item) => navigate(`/product-category/edit/${item._id}`)}
          onView={handleOpenViewModal}
          actions={{
            showView: true,
            showEdit: true,
            showDelete: true,
          }}
          loading={loading}
        />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
      />

      <ViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        data={selectedItem}
        fields={fieldsToShow}
      />
    </div>
  );
};

export default ProductCategory;
